/**
 * PATH: packages/ui/src/mobile/useEdgeSnapDrag.ts
 * PURPOSE: Pointer-drag a floating control, then snap + persist its home.
 * WHY: Click/tap still fires when the pointer barely moved; a real drag
 *      relocates the control instead of activating it.
 */
import { useCallback, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import {
  loadSnapCorner,
  loadSnapEdge,
  saveSnapValue,
  snapCornerFromPoint,
  snapEdgeFromPoint,
  type SnapCorner,
  type SnapEdge,
} from './edgeSnapCorner'

const MOVE_PX = 8

export function useEdgeSnapCornerDrag(storageKey: string, fallback: SnapCorner) {
  const [corner, setCorner] = useState<SnapCorner>(() => loadSnapCorner(storageKey, fallback))
  const drag = useDragTracker()
  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      drag.begin(e)
    },
    [drag],
  )
  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      drag.move(e)
    },
    [drag],
  )
  const onPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      if (!drag.endWasDrag()) return
      e.preventDefault()
      const next = snapCornerFromPoint(e.clientX, e.clientY, window.innerWidth, window.innerHeight)
      setCorner(next)
      saveSnapValue(storageKey, next)
    },
    [drag, storageKey],
  )
  return {
    corner,
    dragged: drag.draggedRef,
    bind: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: drag.cancel },
  }
}

export function useEdgeSnapBarDrag(storageKey: string, fallback: SnapEdge) {
  const [edge, setEdge] = useState<SnapEdge>(() => loadSnapEdge(storageKey, fallback))
  const drag = useDragTracker()
  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      drag.begin(e)
    },
    [drag],
  )
  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      drag.move(e)
    },
    [drag],
  )
  const onPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      if (!drag.endWasDrag()) return
      e.preventDefault()
      const next = snapEdgeFromPoint(e.clientY, window.innerHeight)
      setEdge(next)
      saveSnapValue(storageKey, next)
    },
    [drag, storageKey],
  )
  return {
    edge,
    dragged: drag.draggedRef,
    bind: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: drag.cancel },
  }
}

function useDragTracker() {
  const start = useRef<{ x: number; y: number } | null>(null)
  const draggedRef = useRef(false)
  const begin = useCallback((e: ReactPointerEvent) => {
    start.current = { x: e.clientX, y: e.clientY }
    draggedRef.current = false
    // Capturing on pointer-down steals the click from the button inside
    // this element, so a tap on Menu never reaches onClick. Capture only
    // after the pointer has actually moved.
  }, [])
  const move = useCallback((e: ReactPointerEvent) => {
    const s = start.current
    if (!s) return
    if (Math.hypot(e.clientX - s.x, e.clientY - s.y) > MOVE_PX) {
      draggedRef.current = true
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    }
  }, [])
  const endWasDrag = useCallback(() => {
    const was = draggedRef.current
    start.current = null
    return was
  }, [])
  const cancel = useCallback(() => {
    start.current = null
    draggedRef.current = false
  }, [])
  return { begin, move, endWasDrag, cancel, draggedRef }
}
